import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { PageAccessResponse } from './auth-exchange.model';
import { ACCESS_TOKEN_KEY, AUTH_PAGE_ACCESS_PATH } from './auth.constants';

export type PageAccessResult = 'allowed' | 'denied' | 'auth-required' | 'unavailable';

@Injectable({
  providedIn: 'root'
})
export class PageAccessService {
  readonly #httpClient = inject(HttpClient);
  readonly #baseUrl = `${environment.urlProfileManagement}${AUTH_PAGE_ACCESS_PATH}`;
  readonly #cachedAccess = new Map<string, 'allowed' | 'denied'>();
  readonly #inFlightChecks = new Map<string, Observable<PageAccessResult>>();
  #sessionVersion = 0;

  check(path: string): Observable<boolean> {
    /** @deprecated Prefer RoutePageAccessPolicy.checkAccess() for shell route enforcement. */
    return this.checkDetailed(path).pipe(map((result) => result === 'allowed'));
  }

  checkDetailed(path: string): Observable<PageAccessResult> {
    if (!localStorage.getItem(ACCESS_TOKEN_KEY)) {
      return of('auth-required');
    }

    if (this.#cachedAccess.has(path)) {
      return of(this.#cachedAccess.get(path)!);
    }

    const inFlight = this.#inFlightChecks.get(path);
    if (inFlight) {
      return inFlight;
    }

    const params = new HttpParams().set('path', path);
    const sessionVersion = this.#sessionVersion;
    let request$: Observable<PageAccessResult>;

    request$ = this.#httpClient.get<PageAccessResponse>(this.#baseUrl, { params }).pipe(
      map((response): PageAccessResult => response.allowed ? 'allowed' : 'denied'),
      tap((result) => {
        if (sessionVersion === this.#sessionVersion) {
          this.#cachedAccess.set(path, result as 'allowed' | 'denied');
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return of('auth-required' as const);
        }

        return of('unavailable' as const);
      }),
      finalize(() => {
        if (this.#inFlightChecks.get(path) === request$) {
          this.#inFlightChecks.delete(path);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.#inFlightChecks.set(path, request$);
    return request$;
  }

  invalidateSession(): void {
    this.#sessionVersion += 1;
    this.#cachedAccess.clear();
    this.#inFlightChecks.clear();
  }
}
